import { useEffect, useState, useMemo } from 'react'
import {
    ScoopDrawer,
    ScoopButton,
    ScoopTable,
    ScoopMenu,
    ScoopMenuItem,
    ScoopTooltip,
    ScoopText,
} from '../../../../ui'
import {
    XIcon,
    EyeIcon,
    DotsThreeIcon,
    TextAaIcon,
    TrashIcon,
    KeyIcon,
} from '@phosphor-icons/react'
import styles from './TableBox.module.scss'
import { useModal, MODALS } from '../../../../modals'
import { useReportSeriesMetadata } from '../../../../../api/hooks/datasource/hook'

export const TableBox = ({ table, server, getMetadata, readOnly }) => {
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const rawTableDataModal = useModal(MODALS.SOURCE.RAW_TABLE_DATA)
    const renameTableModal = useModal(MODALS.SOURCE.RENAME_TABLE)
    const propertiesModal = useModal(MODALS.SOURCE.PROPERTIES)

    // Move hook to top so tableMetadata is available for columns
    const {
        reportSeriesMetadata: tableMetadata,
        deleteReportSeries,
        renameReportSeries,
        isRenameLoading,
    } = useReportSeriesMetadata(table.reportSeriesTableID)

    const [openTable, setOpenTable] = useState(false)
    const [rows, setRows] = useState([])
    const [previewColumns, setPreviewColumns] = useState()
    const [previewRows, setPreviewRows] = useState()
    const [ingestions, setIngestions] = useState()
    const [ingestionMap, setIngestionMap] = useState()
    const [dateValue, setDateValue] = useState(null)
    const [ignoreTable, setIgnoreTable] = useState(false)
    const [userTableName, setUserTableName] = useState(null)
    const [anchorEl, setAnchorEl] = useState(null)

    // Define handler before columns so it can be used in useMemo
    const handleOpenProperties = (columnName) => {
        propertiesModal.show({
            profileColumn: columnName,
            tableMetadata,
            server,
        })
    }

    // Use useMemo so columns update when tableMetadata changes
    const columns = useMemo(
        () => [
            {
                field: 'identifier',
                header: 'Key',
                width: 60,
                sortable: false,
                render: ({ value }) => (
                    <div className={styles.keyCellContent}>
                        {value && <KeyIcon size={20} weight="regular" />}
                    </div>
                ),
            },
            {
                field: 'name',
                header: 'Column Name',
                flex: 2,
                sortable: false,
            },
            {
                field: 'measure',
                header: 'Measure/Attribute',
                flex: 1,
                sortable: false,
                valueGetter: ({ row }) => (row.isMeasure ? 'Measure' : 'Attribute'),
            },
            {
                field: 'type',
                header: 'Type',
                flex: 1,
                sortable: false,
            },
            {
                field: '',
                header: '',
                width: 60,
                sortable: false,
                render: ({ row }) => (
                    <div
                        className={styles.profileCellContent}
                        onClick={() => handleOpenProperties(row.name)}
                    >
                        <ScoopTooltip title={'View profile'}>
                            <EyeIcon size={20} weight="regular" />
                        </ScoopTooltip>
                    </div>
                ),
            },
        ],
        [tableMetadata, server]
    )

    useEffect(() => {
        if (tableMetadata) {
            let AuxRows = []
            for (let i = 0; i < tableMetadata.columns.length; i++) {
                if (!tableMetadata.columns[i].changeCol && !tableMetadata.columns[i].dateKey) {
                    AuxRows.push(tableMetadata.columns[i])
                }
            }
            setRows(AuxRows)
        }
    }, [tableMetadata])

    useEffect(() => {
        if (tableMetadata) {
            setUserTableName(tableMetadata.userTableName)
            setIgnoreTable(tableMetadata.tablemetadata.ignoreTable)
        }
    }, [tableMetadata])

    const getIQ = () => {
        let iq = {
            useLatestLoad: true,
            tableID: tableMetadata.reportSeriesID,
        }
        return iq
    }

    const processPreviewData = (previewData) => {
        if (!previewData) return
        setPreviewColumns(
            tableMetadata.columns.map((c) => ({
                field: c.name,
                header: c.name,
                sortable: false,
            }))
        )
        const columnNames = tableMetadata.columns.map((c) => c.name)
        const rows = previewData.rows.map((r) => {
            const parsedRow = {}
            r.forEach((value, i) => {
                if (columnNames.includes(previewData.rows[0][i])) {
                    parsedRow[previewData.rows[0][i]] = value
                }
            })
            return parsedRow
        })
        rows.shift()
        setPreviewRows(rows)
        var newIngestions = []
        var newIngestionMap = new Map()
        for (let i = 0; i < previewData.ingestions.length; i++) {
            newIngestions.push(previewData.ingestions[i].date)
            newIngestionMap.set(previewData.ingestions[i].date, previewData.ingestions[i].key)
        }
        setIngestions(newIngestions)
        if (!dateValue) {
            setDateValue(
                newIngestions.length === 0
                    ? new Date()
                    : new Date(newIngestions[newIngestions.length - 1])
            )
        }
        setIngestionMap(newIngestionMap)
    }

    const updatePreviewData = (iq) => {
        server.postData(
            {
                action: 'reportSeriesTablePreviewData',
                inputQuery: iq,
            },
            processPreviewData
        )
    }

    const processTableContent = (result) => {
        const cleanedUrl = result?.url?.replace(/\s+/g, '')
        window.open(cleanedUrl, '_blank', 'noreferrer')
    }

    const handleTableContent = (rstiKey) => {
        server.postData(
            {
                action: 'getTableContent',
                rstiKey: rstiKey,
            },
            processTableContent
        )
    }

    const handleOpenRawTableData = () => {
        updatePreviewData(getIQ())
    }

    // Effect to show modal when preview data is ready
    useEffect(() => {
        if (previewRows && previewColumns) {
            rawTableDataModal.show({
                tableName: table.tableName,
                previewColumns,
                previewRows,
                ingestions,
                ingestionMap,
                dateValue,
                onDateChange: (newDate) => {
                    const dateStr = `${String(newDate.getMonth() + 1).padStart(2, '0')}/${String(newDate.getDate()).padStart(2, '0')}/${newDate.getFullYear()}`
                    setDateValue(newDate)
                    const iq = getIQ()
                    iq.rstiKey = ingestionMap.get(dateStr)
                    updatePreviewData(iq)
                },
                onDownloadCSV: handleTableContent,
                onClose: () => {
                    setPreviewRows(null)
                    setPreviewColumns(null)
                },
            })
        }
    }, [previewRows, previewColumns])

    const handleOpenRename = () => {
        renameTableModal.show({
            tableName: table.tableName,
            isLoading: isRenameLoading,
            onSave: (newName) => {
                renameReportSeries({
                    newName,
                    ignoreTable,
                    onSuccess: () => {
                        getMetadata()
                        renameTableModal.hide()
                    },
                })
            },
        })
    }

    const handleDeleteTable = async () => {
        deleteReportSeries({
            reportSeriesTableID: table.reportSeriesTableID,
            onSuccess: getMetadata,
        })
    }

    const handleConfirmDeleteTable = async () => {
        await deleteModal.show({
            title: 'Table',
            description: `Are you sure you want to delete ${table.tableName}?`,
            onDelete: handleDeleteTable,
        })
    }

    return (
        <>
            <div className={styles.tableBoxCard} onClick={() => setOpenTable(true)}>
                <div className={styles.cardHeader}>
                    <ScoopText className={styles.cardTitle}>
                        {table.tableName +
                            (table.version > 1 ? ' (version ' + table.version + ')' : '')}
                    </ScoopText>
                    <DotsThreeIcon
                        onClick={(e) => {
                            e.stopPropagation()
                            setAnchorEl(e.currentTarget)
                        }}
                        size={24}
                        weight="regular"
                        className={styles.menuIcon}
                    />
                </div>
                <ScoopText className={styles.cardSubtext}>{`${rows.length} Columns `}</ScoopText>
                <ScoopText className={styles.cardSubtext}>
                    {`Scooped ${tableMetadata?.stats.numIngestions} times`},{' '}
                    {Intl.NumberFormat('en-En').format(tableMetadata?.stats?.rows)} rows
                </ScoopText>
            </div>
            <ScoopDrawer
                open={openTable}
                onClose={() => setOpenTable(false)}
                anchor="right"
                width="650px"
            >
                <div className={styles.drawerContainer}>
                    <div onClick={() => setOpenTable(false)} className={styles.xButton}>
                        <XIcon size={24} weight="regular" />
                    </div>
                    <div className={styles.drawerContent}>
                        <ScoopText className={styles.drawerTitle}>{table.tableName}</ScoopText>
                        <div className={styles.statsContainer}>
                            <div>
                                <ScoopText className={styles.statLabel}>Columns </ScoopText>
                                <ScoopText className={styles.statValue}>{rows?.length}</ScoopText>
                            </div>
                            <div>
                                <ScoopText className={styles.statLabel}>
                                    Number of Rows Ingested{' '}
                                </ScoopText>
                                <ScoopText className={styles.statValue}>
                                    {tableMetadata?.stats.numIngestions}
                                </ScoopText>
                            </div>
                            <div>
                                <ScoopText className={styles.statLabel}>Scooped </ScoopText>
                                <ScoopText className={styles.statValue}>
                                    {`${tableMetadata?.stats.numIngestions} times`}
                                </ScoopText>
                            </div>
                            <div>
                                <ScoopText className={styles.statLabel}>Rows </ScoopText>
                                <ScoopText className={styles.statValue}>
                                    {Intl.NumberFormat('en-En').format(tableMetadata?.stats?.rows)}
                                </ScoopText>
                            </div>
                        </div>
                        <div className={styles.drawerActions}>
                            <ScoopButton size="sm" onClick={handleOpenRawTableData}>
                                View Table Data
                            </ScoopButton>
                        </div>
                        <div className={styles.gridContainer}>
                            {rows && columns && (
                                <ScoopTable
                                    columns={columns}
                                    rows={rows}
                                    rowHeight={48}
                                    headerHeight={48}
                                    emptyMessage="No columns found"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </ScoopDrawer>
            {Boolean(anchorEl) && !readOnly && (
                <ScoopMenu
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                >
                    <ScoopMenuItem
                        onClick={() => {
                            handleOpenRename()
                            setAnchorEl(null)
                        }}
                        leftSection={<TextAaIcon size={18} weight="regular" />}
                    >
                        <ScoopText className={styles.menuItemText}>Rename</ScoopText>
                    </ScoopMenuItem>
                    <ScoopMenuItem
                        onClick={() => {
                            handleConfirmDeleteTable()
                            setAnchorEl(null)
                        }}
                        leftSection={<TrashIcon size={18} weight="regular" />}
                    >
                        <ScoopText className={styles.menuItemDelete}>Delete</ScoopText>
                    </ScoopMenuItem>
                </ScoopMenu>
            )}
        </>
    )
}
